import React, { useState, useEffect } from "react";
import BaseTable from "@components/Tables/BaseTable/BaseTable";
import ScopeStackTable from "@components/Tables/ScopeStackTable";
import NavTabs from "@components/NavTabs/NavTabs";
import { Card, Row, Col, Form } from "react-bootstrap";
import API from "@API";
import ControlledDropDown from "@components/Forms/ControlledDropDown";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import {
  removeDuplicates,
  snakeToPascalCase,
  splitPascalCase,
  toSnakeCase,
  wordCount,
  capitalizeFirstLetter,
  formatUnprocessibleResponse,
} from "@utils/helperFunctions";
import DismissibleAlert from "@components/Alerts/DismissibleAlert";
import ArrowDropdownButton from "@components/Buttons/ArrowDropdownButton/ArrowDropdownButton";

function ThirdPartyServices({
  account_slug,
  project_id,
  account_id,
  projectStatus,
  vendorQuoteId,
  languageFields,
  phases,
  thirdPartyServices,
  subtasks,
  deriveFromServices,
  isLoading,
  authorizationCode,
}) {
  // Env
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const location = window.location.href;
  var locationArr = location.split("/");
  var id = locationArr[locationArr.length - 2];

  // Alerts
  const [updatedAlertIsOpen, setUpdatedAlertIsOpen] = useState(false);
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form/table states
  const [quoteId, setQuoteId] = useState(null);
  const [services, setServices] = useState([]);
  const [subservices, setSubservices] = useState([]);
  const [languageField, setLanguageField] = useState("");

  useEffect(() => {
    for (let i = 0; i < languageFields.length; i++) {
      setLanguageField(languageFields[0].attributes.name);
    }

    const getLanguages = (service) => {
      let newLanguages = languageFields.map((language) => {
        return {
          ...service.attributes.languages,
          [language.attributes["sow-language"]]: "",
        };
      });

      let objOfLanguages = Object.assign({}, ...newLanguages);
      objOfLanguages["service_description"] =
        service.attributes["service-description"] != null
          ? service.attributes["service-description"]
          : "";
      return objOfLanguages;
    };

    const getFrequency = (service) => {
      let frequency = service.attributes["payment-frequency"];
      if (frequency.includes("_")) {
        return splitPascalCase(snakeToPascalCase(frequency));
      }
      return capitalizeFirstLetter(frequency);
    };

    setServices(
      thirdPartyServices.map((service) => {
        let phaseData = service.relationships["project-phase"].data;
        return {
          id: service.id,
          type: "service",
          phase: phaseData ? phaseData.id : "noPhase",
          name: service.attributes.name,
          quantity: service.attributes.quantity,
          hours: service.attributes["est-hours"],
          rate: service.attributes["est-rate"],
          cost: service.attributes["est-cost"],
          frequency: getFrequency(service),
          languages: getLanguages(service),
          changed: false,
        };
      })
    );
    setSubservices(
      subtasks.map((subservice) => {
        return {
          serviceId: subservice.attributes["source-id"],
          id: subservice.id,
          type: "subservice",
          name: subservice.attributes.name,
          quantity: subservice.attributes.quantity,
          hours: subservice.attributes["est-hours"],
          rate: subservice.attributes["est-rate"],
          cost: subservice.attributes["est-cost"],
          frequency: getFrequency(subservice),
          languages: getLanguages(subservice),
          changed: false,
        };
      })
    );

    setQuoteId(id);
  }, [
    languageFields,
    phases,
    thirdPartyServices,
    subtasks,
    deriveFromServices,
    isLoading,
  ]);

  const languageOptions = languageFields.map((languageField) => {
    return (
      <option accessKey={languageField.id} key={languageField.id}>
        {languageField.attributes.name}
      </option>
    );
  });

  const getTableData = (array) => {
    return array.map((item) => {
      const deleteService = () => {
        let userResponse = confirm("Are you sure?");
        if (userResponse === false) {
          return;
        }
        let service = {
          data: {
            type: "third-party-services",
            id: item.id,
          },
        };
        API.Delete(
          `${apiHost}/${account_slug}/v1/third-party-services/${item.id}`,
          service,
          authorizationCode
        )
          .then((response) => {
            if (response.status == 204) {
              window.location.reload();
            }
          })
          .catch((err) => {
            setErrorMessages(
              formatUnprocessibleResponse(err, "Service details")
            );
            setShowFailAlert(true);
          });
      };

      const deleteBtn = (
        <div
          id={item.id}
          className="dropdown-item btn-sm additional-effort"
          onClick={(e) => deleteService()}
        >
          <i className="fa fa-remove"></i>
          Delete
        </div>
      );

      const getColumns = () => {
        if (item.type == "project-phase") {
          return [{ class: "col-12", name: <strong>{item.name}</strong> }];
        } else {
          const serviceNameField = {
            class: "col",
            name: item.name,
          };

          const descriptionField = {
            class: "col-6",
            name: languageFields.map((language, index) => (
              <Form.Control
                key={index}
                as="textarea"
                rows={2}
                style={
                  language.attributes.name !== languageField
                    ? { display: "none" }
                    : { display: "block" }
                }
                id={item.id}
                value={item.languages[language.attributes["sow-language"]]}
                onChange={(e) => {
                  e.persist();
                  setHasUnsavedChanges(true);
                  if (item.type == "subservice") {
                    setSubservices(
                      subservices.map((subservice) => {
                        let langArray = languageFields.map((lang) => {
                          if (
                            lang.attributes["sow-language"] ===
                            language.attributes["sow-language"]
                          ) {
                            let key = lang.attributes["sow-language"];
                            return {
                              ...subservice.languages,
                              [key]: e.target.value,
                            };
                          }
                        });
                        let newLangs = Object.assign({}, ...langArray);
                        if (subservice.id == e.target.id) {
                          return {
                            ...subservice,
                            changed: true,
                            languages: newLangs,
                          };
                        } else {
                          return subservice;
                        }
                      })
                    );
                  }
                  if (item.type !== "subservice") {
                    setServices(
                      services.map((service) => {
                        let langArray = languageFields.map((lang) => {
                          if (
                            lang.attributes["sow-language"] ===
                            language.attributes["sow-language"]
                          ) {
                            let key = lang.attributes["sow-language"];
                            return {
                              ...service.languages,
                              [key]: e.target.value,
                            };
                          }
                        });
                        let newLangs = Object.assign({}, ...langArray);
                        if (service.id == e.target.id) {
                          return {
                            ...service,
                            changed: true,
                            languages: newLangs,
                          };
                        } else {
                          return service;
                        }
                      })
                    );
                  }
                }}
              />
            )),
          };

          const arrowButtonColumn = {
            class: "arrowDropdownColWidth",
            name: (
              <div className="actionsDiv">
                <ArrowDropdownButton id={item.id} options={deleteBtn} />
              </div>
            ),
          };

          return [serviceNameField, descriptionField, arrowButtonColumn];
        }
      };
      return {
        rowClass:
          item.type !== "subservice"
            ? "thirdPartyServicesRow row"
            : "thirdPartyServicesRow row subserviceRow",
        onClick: null,
        columns: getColumns(),
        actions: null,
      };
    });
  };

  let thirdPartyServicesHeaderData = {
    rowClass: "thirdPartyServicesHeaderRow row",
    columns: [
      {
        class: "col",
        name: "Service",
      },
      {
        class: "col-6",
        name: "Content",
      },
    ],
  };

  const getThirdPartyServicesFooterData = () => {
    let content = "";
    content = (
      <div>
        <SubmitButton />
      </div>
    );
    return {
      rowClass: "col-12",
      columns: [{ class: "thirdPartyServicesFooter", content: content }],
    };
  };

  const arrangeDataForTable = () => {
    let array = [];
    let phasesWithServices = new Set();
    for (let i = 0; i < phases.length; i++) {
      let phaseServices = services.filter(
        (service) => service.phase == phases[i].id
      );
      if (phaseServices.length > 0) phasesWithServices.add(phases[i]);
    }

    let noPhaseServices = services.filter(
      (service) => service.phase == null || service.phase == "noPhase"
    );
    if (noPhaseServices.length > 0) {
      phasesWithServices.add({
        id: "noPhase",
        attributes: { name: "No Phase" },
      });
    }
    phasesWithServices = Array.from(phasesWithServices);

    for (let i = 0; i < phasesWithServices.length; i++) {
      array.push({
        type: "project-phase",
        name: phasesWithServices[i].attributes.name,
      });
      for (let k = 0; k < services.length; k++) {
        let phaseId = services[k].phase;
        if (phaseId == phasesWithServices[i].id) {
          array.push(services[k]);
          for (let j = 0; j < subservices.length; j++) {
            if (subservices[j].serviceId == services[k].id) {
              array.push(subservices[j]);
            }
          }
        }
      }
    }
    return array;
  };

  const adjustedServices = arrangeDataForTable();

  const thirdPartyServicesTableData = getTableData(adjustedServices);
  const thirdPartyServicesFooterData = getThirdPartyServicesFooterData();

  let thirdPartyServicesTable = new ScopeStackTable(
    "services",
    thirdPartyServicesHeaderData,
    thirdPartyServicesTableData,
    thirdPartyServicesFooterData
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setHasUnsavedChanges(false);
    let promises = [];

    const getUpdatedLanguages = (service) => {
      let languages = service.languages;
      delete service.languages["service_description"];
      return languages;
    };

    const submitSubservices = (serviceId, postedServiceId, phaseId) => {
      subservices.forEach((subservice) => {
        let subserviceData = {
          data: {
            type: "third-party-services",
            id: subservice.id,
            attributes: {
              position: subservice.id,
              "service-description":
                subservice.languages["service_description"],
              quantity: subservice.quantity,
              "est-cost": subservice.cost,
              "est-hours": subservice.hours,
              "est-rate": subservice.rate,
              "payment-frequency":
                wordCount(subservice.frequency) == 2
                  ? toSnakeCase(subservice.frequency)
                  : subservice.frequency.toLowerCase(),
              "source-type": "ThirdPartyService",
              "source-id": postedServiceId,
            },
            relationships: {
              "project-phase": {
                data: { type: "project-phases", id: phaseId },
              },
            },
          },
        };
        if (subservice.serviceId == serviceId && subservice.changed === true) {
          subserviceData.data.attributes.languages =
            getUpdatedLanguages(subservice);
          promises.push(
            API.Patch(
              `${apiHost}/${account_slug}/v1/third-party-services/${subservice.id}`,
              subserviceData,
              authorizationCode
            )
          );
        }
      });
    };

    services.forEach((service, index) => {
      if (subservices.length !== 0) {
        submitSubservices(service.id, service.id, service.phase);
      }
      if (service.changed === true) {
        let serviceData = {
          data: {
            type: "third-party-services",
            id: service.id,
            attributes: {
              position: index + 1,
              "service-description": service.languages["service_description"],
              languages: getUpdatedLanguages(service),
              quantity: service.quantity,
              "est-cost": service.cost,
              "est-hours": service.hours,
              "est-rate": service.rate,
              "payment-frequency":
                wordCount(service.frequency) == 2
                  ? toSnakeCase(service.frequency)
                  : service.frequency.toLowerCase(),
              "source-type": "Quote",
              "source-id": quoteId,
            },
            relationships: {
              "project-phase": {
                data: {
                  type: "project-phases",
                  id: service.phase === "noPhase" ? null : service.phase,
                },
              },
            },
          },
        };
        promises.push(
          API.Patch(
            `${apiHost}/${account_slug}/v1/third-party-services/${service.id}`,
            serviceData,
            authorizationCode
          )
        );
      }
    });

    Promise.all(promises)
      .then((response) => {
        let allSuccessful = true;
        response.forEach((res) => {
          let data = res.data.data;
          if (
            (res.status == 200 || res.status == 201) &&
            data.attributes["source-type"] == "Quote"
          ) {
            setServices(
              services.map((service) => {
                if (service.id == data.id) {
                  return {
                    ...service,
                    languages: {
                      ...service.languages,
                      service_description:
                        data.attributes["service-description"],
                    },
                  };
                } else {
                  return service;
                }
              })
            );
          }
          if (
            (res.status == 200 || res.status == 201) &&
            data.attributes["source-type"] == "ThirdPartyService"
          ) {
            setSubservices(
              subservices.map((subservice) => {
                if (subservice.id == data.id) {
                  let languages = data.attributes["original-languages"];
                  languages["service_description"] =
                    data.attributes["service-description"];
                  return {
                    ...subservice,
                    languages: languages,
                  };
                } else {
                  return subservice;
                }
              })
            );
          }
          if (res.status !== 200 && res.status !== 201) {
            allSuccessful = false;
          }
        });
        if (allSuccessful) {
          setUpdatedAlertIsOpen(true);
          setTimeout(() => {
            setUpdatedAlertIsOpen(false);
          }, 3000);
        }
      })
      .catch((err) => {
        setErrorMessages(formatUnprocessibleResponse(err, "Service details"));
        setShowFailAlert(true);
      });
  };

  const serviceUpdatedAlert = () => {
    if (updatedAlertIsOpen === true) {
      return (
        <DismissibleAlert
          className="Alerts"
          variant="info"
          onClose={() => setUpdatedAlertIsOpen(false)}
          text="Service details updated"
        />
      );
    }
  };

  const handleFailAlert = () => {
    if (showFailAlert === true) {
      return (
        <DismissibleAlert
          variant="warning"
          onClose={() => setShowFailAlert(false)}
          text={errorMessages}
        />
      );
    }
  };

  return (
    <div>
      <NavTabs
        tabOneText="Vendor Quote"
        tabOneUrl={`/projects/${project_id}/quotes/${quoteId}/edit`}
        tabTwoText="Vendor Description"
        tabTwoUrl={
          services.length !== 0
            ? `/projects/${project_id}/quotes/${quoteId}/quoted_service`
            : `/projects/${project_id}/quotes/${quoteId}/quoted_service/new`
        }
        tabThreeText="Our Description"
        tabThreeUrl={`/projects/${project_id}/quotes/${quoteId}/third_party_services`}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <div className="col-sm-12" id="sow-workarea">
        <div className="tabContent">
          {serviceUpdatedAlert()}
          {handleFailAlert()}
          <Card>
            <Card.Body className="no-gutters">
              <Row>
                <Col></Col>
                <Col sm={3}>
                  <ControlledDropDown
                    className="pull-right"
                    label="View"
                    value={languageField}
                    onChange={(e) => setLanguageField(e.target.value)}
                    options={languageOptions}
                  />
                </Col>
              </Row>
              <Form onSubmit={handleSubmit}>
                <BaseTable
                  className="thirdPartyServicesTable"
                  striped={false}
                  hover={false}
                  bordered={false}
                  headerRows={thirdPartyServicesTable.buildHeaderRows()}
                  dataRows={thirdPartyServicesTable.buildDataRows()}
                  footerRows={thirdPartyServicesTable.buildFooterRows()}
                  isLoading={isLoading}
                />
              </Form>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ThirdPartyServices;
